import L from 'leaflet'
import { googleSession, store, TILE_BASE } from './googleTiles'
import { googleMapsKey, SATELLITE_MAX_ZOOM, type TileSource } from './mapTiles'

// What every Leaflet map in CONTROL draws under its marks: Google's satellite
// imagery (the Map Tiles API) with a Map / Satellite switch, where the build
// has a Maps key (VITE_GOOGLE_MAPS_KEY), else the configured tiles alone.
//
// Until Google's session token arrives (googleTiles.ts), and when it cannot be
// had (the API refused), the map shows the street tiles, so a map is never
// blank. Google's terms ask for its logo and the copyright of the imagery in
// view, read from the viewport endpoint as the map moves.
//
// Which of the two the person last chose is a convenience kept in this
// browser; Satellite is the default.

const CHOICE_STORE = 'dp.map.base'

const GOOGLE_LOGO = '<span class="dp-map-google">Google</span>'

// Adds the base layers to `map`; returns what removes the switch again (the
// layers go with the map).
export function addBaseLayers(map: L.Map, tiles: TileSource): () => void {
  const street = L.tileLayer(tiles.url, {
    attribution: tiles.attribution,
    maxNativeZoom: tiles.nativeZoom ?? tiles.maxZoom,
    maxZoom: tiles.maxZoom,
  }).addTo(map)
  const key = googleMapsKey()
  if (!tiles.satellite || !key) return () => {}

  let gone = false
  let control: L.Control.Layers | null = null
  let satellite: L.TileLayer | null = null
  let copyright = ''
  let timer: ReturnType<typeof setTimeout> | null = null

  const setCopyright = (text: string) => {
    const a = map.attributionControl
    if (!a) return
    if (copyright) a.removeAttribution(copyright)
    copyright = text
    if (copyright) a.addAttribution(copyright)
  }

  const readCopyright = (session: string) => {
    if (gone) return
    if (!satellite || !map.hasLayer(satellite)) return setCopyright('')
    const b = map.getBounds()
    const q = new URLSearchParams({
      session,
      key,
      zoom: String(Math.round(map.getZoom())),
      north: String(b.getNorth()),
      south: String(b.getSouth()),
      east: String(b.getEast()),
      west: String(b.getWest()),
    })
    fetch(`${TILE_BASE}/tile/v1/viewport?${q}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((v: { copyright?: string } | null) => {
        if (gone || !satellite || !map.hasLayer(satellite)) return
        const text = String(v?.copyright || '').replace(/[<>&]/g, '')
        setCopyright(`${GOOGLE_LOGO}${text ? ` ${text}` : ''}`)
      })
      .catch(() => {})
  }

  let later = () => {}
  const chosen = (e: L.LayersControlEvent) => {
    store.set(CHOICE_STORE, e.layer === street ? 'map' : 'satellite')
    later()
  }

  void googleSession(key).then((s) => {
    if (gone || !s) return
    satellite = L.tileLayer(`${TILE_BASE}/v1/2dtiles/{z}/{x}/{y}?session=${encodeURIComponent(s.session)}&key=${encodeURIComponent(key)}`, {
      maxZoom: SATELLITE_MAX_ZOOM,
      maxNativeZoom: SATELLITE_MAX_ZOOM,
    })
    later = () => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => readCopyright(s.session), 400)
    }
    control = L.control.layers({ Satellite: satellite, Map: street }, undefined, { position: 'topright', collapsed: false }).addTo(map)
    if (store.get(CHOICE_STORE) !== 'map') {
      map.removeLayer(street)
      satellite.addTo(map)
    }
    map.on('baselayerchange', chosen)
    map.on('moveend', later)
    later()
  })

  return () => {
    gone = true
    if (timer) clearTimeout(timer)
    map.off('baselayerchange', chosen)
    map.off('moveend', later)
    control?.remove()
  }
}
